package presentation.screens.main

import com.kinoshazam.Navigator
import com.kinoshazam.data.GlobalStateRepository
import com.kinoshazam.model.Route
import data.VideoRepository
import data.local.DelightLocalRepository
import features.videoDetails.data.FavoriteInteractor
import features.videoDetails.data.model.FavoriteEntity
import features.watch.WatchRules
import features.watch.WatchProgressInteractor
import features.watch.model.WatchEntry
import models.User
import presentation.screens.main.MainState.DrawerStateValue
import presentation.screens.main.MainState.MenuItem
import utils.BaseViewModel
import utils.launch


class MainViewModel(
    private val moviesRepository: VideoRepository,
    private val favoriteInteractor: FavoriteInteractor,
    private val watchProgressInteractor: WatchProgressInteractor,
    private val delightLocalRepository: DelightLocalRepository,
    private val navigator: Navigator,
    private val globalStateRepository: GlobalStateRepository,
) : BaseViewModel<MainState, MainMVIAction>() {

    override fun initState(): MainState = MainState(
        watchVideo = globalStateRepository.features.value.watchVideo,
        menuItems = MenuItem.entries.filter { it != MenuItem.FAVORITES },
    )

    private var currentPage: Int = 1

    init {
        loadVideos()
        authInit()
        updateInit()
        languageInit()
        loadFavorite()
        featuresInit()
        observeContinue()
    }

    override fun onAction(action: MainMVIAction) {
        when (action) {
            is MainMVIAction.FavoriteOpened -> loadFavorite()

            is MainMVIAction.Reload -> {
                updateState {
                    copy(
                        isLoading = true,
                        isError = false,
                        isMainError = false,
                        isCartoonError = false,
                        isFavoriteError = false,
                        soonMovies = null,
                        newMovies = null,
                        newTvShows = null,
                        cartoonsVideos = null,
                        cartoonsTvVideos = null,
                    )
                }
                loadVideos()
            }

            is MainMVIAction.ClickBackWithDrawer -> {
                updateState { copy(drawerState = DrawerStateValue.CLOSED) }
            }

            is MainMVIAction.OnDrawerOpened -> {
                updateState { copy(drawerState = DrawerStateValue.OPENED) }
            }

            is MainMVIAction.OnMenuClicked -> {
                updateState { copy(drawerState = DrawerStateValue.OPEN) }
            }

            is MainMVIAction.FetchNextPage -> fetchNextPage()

            is MainMVIAction.OnMenuItemClicked -> {
                updateState {
                    val next = action.menuItem
                    copy(
                        selectedMenu = next,
                        drawerState = DrawerStateValue.CLOSED,
                        menuItems = favoriteList?.let { menuFor(it, next) } ?: menuItems,
                    )
                }
            }

            is MainMVIAction.OnAllVideoClicked -> {
                navigator.navigateTo(
                    Route.AllVideo(
                        screenType = action.screenType.name,
                        title = action.title,
                        genre = action.genre.orEmpty()
                    )
                )
            }

            is MainMVIAction.OnDetailsClicked -> {
                updateState { copy(lastFocusedVideoId = action.id) }
                navigator.navigateTo(
                    Route.Dialog.Details(action.id, false)
                )
            }

            is MainMVIAction.OnSearchClicked -> {
                navigator.navigateTo(
                    Route.Dialog.Search
                )
            }

            is MainMVIAction.OnTvShowClicked -> {
                updateState { copy(lastFocusedVideoId = action.id) }
                navigator.navigateTo(
                    Route.Dialog.Details(action.id, true)
                )
            }

            is MainMVIAction.ClearLastFocusedVideo -> {
                updateState { copy(lastFocusedVideoId = null) }
            }

            is MainMVIAction.AppInfo -> {
                updateState { copy(drawerState = DrawerStateValue.CLOSED) }
                navigator.navigateTo(Route.AppInfo)
            }

            is MainMVIAction.UpdateClicked -> {
                updateState { copy(drawerState = DrawerStateValue.CLOSED) }
                navigator.navigateTo(Route.Update)
            }

            is MainMVIAction.ProfileClicked -> {
                updateState { copy(drawerState = DrawerStateValue.CLOSED) }
                navigator.navigateTo(Route.Dialog.Profile)
            }

            is MainMVIAction.DecideClicked -> {
                navigator.navigateTo(Route.Dialog.Decide())
            }

            is MainMVIAction.OpenContinue -> openContinue(action.entry)

            is MainMVIAction.HideContinue -> {
                launch { watchProgressInteractor.hideFromContinue(action.entry) }
            }
        }
    }

    private fun observeContinue() {
        launch {
            watchProgressInteractor.observe().collect { entries ->
                updateState {
                    copy(
                        continueWatching = WatchRules.continueList(entries),
                        continueWatchingReady = true,
                    )
                }
            }
        }
    }

    private fun openContinue(entry: WatchEntry) {
        navigator.navigateTo(Route.Dialog.Details(entry.videoIdInt, entry.isTvShow))
        val url = entry.embedUrl() ?: return
        navigator.navigateTo(
            Route.Dialog.WebPlayer(
                url = url,
                title = entry.title.orEmpty(),
                videoId = entry.videoIdInt,
                isTvShow = entry.isTvShow,
                seasonNumber = entry.seasonNumber,
                episodeNumber = entry.episodeNumber,
                imdbId = entry.imdbId.orEmpty(),
                imageUrl = entry.imageUrl.orEmpty(),
                backdropUrl = entry.backdropUrl.orEmpty(),
            )
        )
    }

    private fun loadVideos() {
        launch(onError = {
            updateState {
                copy(
                    isLoading = false,
                    isError = true,
                    soonMovies = soonMovies ?: emptyList(),
                    newMovies = newMovies ?: emptyList(),
                    newTvShows = newTvShows ?: emptyList(),
                    cartoonsVideos = cartoonsVideos ?: emptyList(),
                    cartoonsTvVideos = cartoonsTvVideos ?: emptyList(),
                )
            }
        }) {
            val genres = moviesRepository.getGenreList()
            // TODO: move to cartoon menu selected
            launch(
                onError = {
                    updateState {
                        copy(
                            isCartoonError = true,
                            cartoonsVideos = cartoonsVideos ?: emptyList(),
                            cartoonsTvVideos = cartoonsTvVideos ?: emptyList(),
                        )
                    }
                }
            ) {
                val cartoon = moviesRepository.getCartoons(page = 1) ?: emptyList()
                val cartoonTv = moviesRepository.getCartoonsTV(page = 1) ?: emptyList()
                updateState {
                    copy(
                        cartoonsVideos = cartoon,
                        cartoonsTvVideos = cartoonTv,
                    )
                }
            }
            launch(
                onError = {
                    updateState {
                        copy(
                            isMainError = true,
                            isLoading = false,
                            soonMovies = soonMovies ?: emptyList(),
                            newMovies = newMovies ?: emptyList(),
                            newTvShows = newTvShows ?: emptyList(),
                        )
                    }
                }
            ) {
                val moviesSoon =
                    moviesRepository.getReleaseSoonMovies(page = 1) ?: emptyList()
                val movies = moviesRepository.getNewMovies(page = 1) ?: emptyList()
                val tvShows = moviesRepository.getTrendingTv(page = 1) ?: emptyList()
                updateState {
                    copy(
                        isLoading = false,
                        isError = false,
                        genres = genres,
                        soonMovies = moviesSoon,
                        newMovies = movies,
                        newTvShows = tvShows,
                    )
                }
            }
        }
    }

    private fun fetchNextPage() {
        launch(onError = {
            updateState { copy(isNextLoading = false) }
        }) {
            updateState { copy(isNextLoading = true, isError = false) }
            loadVideosByPage(++currentPage)
        }
    }

    private suspend fun loadVideosByPage(page: Int) {
        when (state.value.selectedMenu) {
            MenuItem.FILMS -> {
                val nextVideoList = moviesRepository.getTrendingMovies(page = page)
                updateState {
                    copy(
                        isNextLoading = false,
                        isError = false,
                        newMovies = nextVideoList?.let {
                            stateValue.newMovies?.plus(nextVideoList)
                        },
                    )
                }
            }

            MenuItem.TVSHOWS -> {
                val nextVideoList = moviesRepository.getTrendingTv(page = page)
                updateState {
                    copy(
                        isNextLoading = false,
                        isError = false,
                        newTvShows = nextVideoList?.let {
                            stateValue.newTvShows?.plus(nextVideoList)
                        },
                    )
                }
            }

            MenuItem.CARTOONS -> {
                val nextVideoList = moviesRepository.getCartoons(page = page)
                updateState {
                    copy(
                        isNextLoading = false,
                        isError = false,
                        cartoonsVideos = nextVideoList?.let {
                            stateValue.cartoonsVideos?.plus(nextVideoList)
                        },
                    )
                }
            }

            MenuItem.FAVORITES -> {
                // TODO: get more favorite data when will be getting form server
                updateState {
                    copy(
                        isNextLoading = false,
                        isError = false,
                    )
                }
            }

            else -> Unit
        }
    }

    private fun loadFavorite() {
        launch(onError = {
            updateState { copy(isFavoriteError = true) }
        }) {
            favoriteInteractor.getFavoriteList().collect { incoming ->
                updateState {
                    // Do not leave the open favorites tab when the list flickers empty.
                    // Refresh restores rows after the first paint, and account sync
                    // replaces them in one step. Either one used to bounce the address to /.
                    copy(
                        favoriteList = incoming,
                        menuItems = menuFor(incoming, selectedMenu),
                        isError = false,
                    )
                }
            }
        }
        launch {
            favoriteInteractor.syncing.collect { syncing ->
                updateState { copy(isFavoriteSync = syncing) }
            }
        }
    }

    private fun menuFor(favorites: List<FavoriteEntity>, selected: MenuItem): List<MenuItem> {
        return MenuItem.entries.filter {
            it != MenuItem.FAVORITES || favorites.isNotEmpty() || selected == MenuItem.FAVORITES
        }
    }

    private fun authInit() {
        launch {
            globalStateRepository.profile.collect { profile ->
                updateState {
                    copy(
                        user = profile?.let {
                            User(
                                userName = it.userName,
                                email = it.email,
                                avatar = it.avatar,
                            )
                        }
                    )
                }
            }
        }
    }

    private fun updateInit() {
        launch {
            globalStateRepository.appUpdate.collect {
                updateState { copy(updateAvailable = it.isNeedUpdate) }
            }
        }
    }

    private fun featuresInit() {
        launch {
            globalStateRepository.features.collect {
                updateState { copy(tasteEnabled = it.taste, watchVideo = it.watchVideo) }
            }
        }
    }

    private fun languageInit() {
        launch {
            var isFirst = true
            globalStateRepository.appLanguage.collect {
                // Initial catalog load already runs from init {}; only reload on changes.
                if (isFirst) {
                    isFirst = false
                    return@collect
                }
                loadVideos()
            }
        }
    }
}